import {channelName} from '../../line_wizard/accountsList';
import {FACEBOOK_CHANNEL, KLAVIYO_CHANNEL, MERCADO_ADS_CHANNEL, SEARCH_CONSOLE_CHANNEL, TIKTOK_CHANNEL} from '../../channels/channels';
import {mercadoAdsConnectionName, sellerText} from '../../channels/mercado_ads/mercadoAds';
import {isStoreChannel, unitOf} from './platformsModel';

/**
 * Los textos de Plataformas que salen de combinar datos: el resumen de una plataforma, «Ver las N
 * cuentas», la línea del aviso de atención. Reciben el `t` de quien dibuja, así se testean sin React.
 *
 * Todo lo que se interpola es dato (el nombre de un acceso, un canal como «Display & Video 360»), y
 * i18next escapa `/ & < >` a entidades si no se le pide lo contrario: por eso `RAW` va en cada uno.
 */

const NS = 'agency_v2.platforms';
const RAW = {escapeValue: false};

// «Search Console» a secas, como el banner de credenciales caídas y el mockup: «Google Search
// Console» partía en tres renglones el título de su tarjeta y su «Conectar …».
const NAME_OVERRIDES = {[SEARCH_CONSOLE_CHANNEL]: 'Search Console'};

/** El nombre comercial del canal («Meta Ads», no «Facebook Ads»), el mismo del armado de líneas. */
export function platformName(channel) {
    return NAME_OVERRIDES[channel] || channelName(channel);
}

/** Un día, sin hora («13 feb 2026»). Los vencimientos van en hora de Argentina, como el mail que los avisa. */
export function formatDay(iso, language, {argentina = false} = {}) {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    const options = {day: 'numeric', month: 'short', year: 'numeric'};
    if (argentina) options.timeZone = 'America/Argentina/Buenos_Aires';
    return date.toLocaleDateString(language || 'es', options);
}

/** «N cuentas», «N propiedades», «N organizaciones» o «N tiendas», según el canal. */
export function unitCountText(t, channel, count) {
    switch (unitOf(channel)) {
        case 'properties':
            return t(`${NS}.summary_properties`, {count});
        case 'organizations':
            return t('agency_connections.escalafy_orgs', {count});
        case 'stores':
            return t(`${NS}.summary_stores`, {count});
        default:
            return t(`${NS}.summary_accounts`, {count});
    }
}

/** El resumen de la fila de una plataforma: «2 accesos · 1.195 cuentas», o «2 tiendas». */
export function platformSummary(t, platform) {
    // Sin accesos ni cuentas que contar: se nombra la integración, para que la fila no quede muda.
    if (platform.legacyGa4) return t(`${NS}.summary_legacy_ga4`);
    if (isStoreChannel(platform.channel)) return unitCountText(t, platform.channel, platform.stores.length);
    return `${t(`${NS}.summary_access`, {count: platform.credentials.length})} · ${unitCountText(t, platform.channel, platform.accountsCount)}`;
}

/**
 * El link del pie de una plataforma, que lleva a la pestaña Cuentas filtrada. `null` cuando no hay
 * nada que ver: un «Ver las 0 cuentas» manda a una lista vacía.
 */
export function seeAccountsText(t, platform) {
    const count = platform.accountsCount;
    if (!count) return null;
    switch (unitOf(platform.channel)) {
        case 'stores':
            return t(`${NS}.see_stores`);
        case 'properties':
            return count === 1 ? t(`${NS}.see_property`) : t(`${NS}.see_properties`, {count});
        case 'organizations':
            return count === 1 ? t(`${NS}.see_organization`) : t(`${NS}.see_organizations`, {count});
        default:
            return count === 1 ? t(`${NS}.see_account`) : t(`${NS}.see_accounts`, {count});
    }
}

/**
 * El rótulo de estado de una plataforma, o `null` si no lleva (GA4 por planilla).
 *
 * «N accesos vencidos» sólo si TODOS vencieron: un acceso que se desconectó no venció, y en la misma
 * pantalla el aviso dice «se desconectó» y el punto rojo del segmentado «por reconectar». En Klaviyo
 * se habla de cuenta, no de tienda: viaja como tienda pero es una cuenta de email.
 */
export function statusText(t, platform) {
    const {status} = platform;
    const isAccount = platform.channel === KLAVIYO_CHANNEL;
    switch (status.kind) {
        case 'connected':
            return t(`${NS}.status.connected`);
        case 'attention':
            return t(`${NS}.status.${status.allExpired ? 'attention' : 'reconnect'}`, {count: status.count});
        case 'expiring':
            return t(`${NS}.status.expiring`, {count: status.count});
        case 'store_no_access':
            return t(`${NS}.status.${isAccount ? 'account_no_access' : 'store_no_access'}`, {count: status.count});
        case 'store_unassigned':
            return t(`${NS}.status.${isAccount ? 'account_unassigned' : 'store_unassigned'}`, {count: status.count});
        default:
            return null;
    }
}

/**
 * El nombre de un acceso; sin nombre, el de siempre del clásico. En Mercado Ads, el apodo del vendedor
 * mientras la conexión conserve el nombre de fábrica (el id numérico de Mercado Libre).
 */
export function accessLabel(t, credential, channel = null) {
    const name = channel === MERCADO_ADS_CHANNEL ? mercadoAdsConnectionName(credential) : credential.label;
    return name || t('credential_row.unknown_label');
}

/** Una línea del aviso de arriba: qué acceso, qué le pasó y desde cuándo. */
export function attentionText(t, item, language) {
    const channel = platformName(item.channel);
    const name = accessLabel(t, item.credential, item.channel);
    if (item.kind === 'disconnected') {
        return t(`${NS}.attention_disconnected`, {channel, name, interpolation: RAW});
    }
    const date = formatDay(item.date, language, {argentina: true});
    const key = item.kind === 'expired' ? 'attention_expired' : 'attention_expiring';
    return t(`${NS}.${key}`, {channel, name, date, interpolation: RAW});
}

/**
 * Quién autorizó el acceso EN LA PLATAFORMA («Usuario de Facebook: Lucía Britez»), o `null`. Es de
 * quién son las cuentas que trae: la plataforma comparte sólo las de esa persona, y el nombre del
 * acceso lo escribe la agencia («meta ads») y no lo dice (cc#209). Meta y TikTok lo dan, y Mercado Ads
 * («Vendedor: APODO»), con el apodo de Mercado Libre.
 */
export function platformUserText(t, channel, credential) {
    if (channel === MERCADO_ADS_CHANNEL) return sellerText(t, credential);
    if (!credential || !credential.platform_user_name) return null;
    if (channel !== FACEBOOK_CHANNEL && channel !== TIKTOK_CHANNEL) return null;
    return t(`${NS}.access.platform_user.${channel}`, {name: credential.platform_user_name, interpolation: RAW});
}

/**
 * Quién y cuándo conectó un acceso. El backend lo sabe en Escalafy y, desde el 2026-10 (cc#209), en
 * las conexiones nuevas de Google, Meta, TikTok y LinkedIn; de las anteriores, sólo cuándo.
 */
export function connectedByText(t, credential, language) {
    const date = formatDay(credential.created_at, language);
    if (!date) return null;
    if (credential.created_by_user_name) {
        return t(`${NS}.access.connected_by`, {user: credential.created_by_user_name, date, interpolation: RAW});
    }
    return t(`${NS}.access.connected_on`, {date, interpolation: RAW});
}

/**
 * Lo que dice la fila de un acceso de Mercado Ads cortado (el vendedor revocó el acceso o cambió la
 * contraseña), o `null`: vale también cuando lo conectó el cliente y la agencia no lo puede reconectar.
 */
export function mercadoAdsDisconnectedText(t, channel, credential) {
    if (channel !== MERCADO_ADS_CHANNEL || !credential || credential.status !== 'DISCONNECTED') return null;
    const nickname = mercadoAdsConnectionName(credential) || t('credential_row.unknown_label');
    return t('connections.mercado_ads.disconnected', {nickname, interpolation: RAW});
}

/** La advertencia debajo del nombre de un acceso que necesita atención (o `null`). */
export function accessWarningText(t, kind, credential, language) {
    if (kind === 'expired') {
        return t(`${NS}.access.expired`, {date: formatDay(credential.expires_at, language, {argentina: true}), interpolation: RAW});
    }
    if (kind === 'expiring') {
        return t(`${NS}.access.expiring`, {date: formatDay(credential.expires_at, language, {argentina: true}), interpolation: RAW});
    }
    if (kind === 'disconnected') {
        const date = formatDay(credential.disconnected_since, language);
        return date
            ? t(`${NS}.access.disconnected`, {date, interpolation: RAW})
            : t(`${NS}.access.disconnected_undated`);
    }
    return null;
}

/** Las iniciales de un cliente, para su avatar en la fila de una tienda. */
export function initialsOf(name) {
    return String(name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
}
